// axiosCentral.js
import axios from "axios";
import { BASE_URL } from "../utils/constants";
import { logoutUserDirect } from "../../features/auth/api";

// Create a centralized Axios instance.
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 60000, // 30 seconds timeout
  headers: {
    "Content-Type": "application/json",
  },
  // withCredentials is set per request in our helper functions.
});

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
     const token = localStorage.getItem("accessToken");
       // make sure it's a real JWT: three segments separated by dots
    if (token && token.split(".").length === 3) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor that forces logout on 401 errors.
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // If the server returns a 401 error, force logout the user.
    if (error.response?.status === 401) {
      console.warn("Access token is invalid. Forcing logout...");
      logoutUserDirect();
    }
    
    console.error(
      "API error:",
      error.response?.status,
      error.response?.data || error.message
    );
    return Promise.reject(error);
  }
);

/**
 * Helper function to perform API requests.
 * The `requiresAuth` flag determines if `withCredentials` should be enabled.
 */
const apiRequest = async (method, endpoint, data = null, params = {}, requiresAuth = false) => {
  try {
    const config = {
      method,
      url: endpoint,
      data,
      params,
    };
    const response = await api(config);
    return response.data;
  } catch (error) {
    console.error(
      "API error:",
      error.response?.status,
      error.response?.data || error.message
    );
    throw error;
  }
};

// Export helper functions for common HTTP methods.
export const getRequest = (endpoint, params = {}, requiresAuth = false) =>
  apiRequest("get", endpoint, null, params, requiresAuth);
export const postRequest = (endpoint, data, requiresAuth = false) =>
  apiRequest("post", endpoint, data, {}, requiresAuth);
export const putRequest = (endpoint, data, requiresAuth = false) =>
  apiRequest("put", endpoint, data, {}, requiresAuth);
export const deleteRequest = (endpoint, requiresAuth = false) =>
  apiRequest("delete", endpoint, null, {}, requiresAuth);
export const patchRequest = (endpoint, data, requiresAuth = false) =>
  apiRequest("patch", endpoint, data, {}, requiresAuth);

export default api;
